<template>
  <v-container fluid class="pt-0">
    <v-card>
      <v-card-title>Transfer SDK Key</v-card-title>
      <v-card-text>
        <v-stepper v-model="currentStep" flat>
          <v-stepper-header>
            <v-stepper-item :value="1" title="Select Donor" :complete="currentStep > 1"></v-stepper-item>
            <v-divider></v-divider>
            <v-stepper-item :value="2" title="Select Key" :complete="currentStep > 2"></v-stepper-item>
            <v-divider></v-divider>
            <v-stepper-item :value="3" title="Select Recipient" :complete="currentStep > 3"></v-stepper-item>
            <v-divider></v-divider>
            <v-stepper-item :value="4" title="Confirm" :complete="transferComplete"></v-stepper-item>
          </v-stepper-header>

          <v-stepper-window>
            <!-- Step 1: Select Donor -->
            <v-stepper-window-item :value="1">
              <v-text-field
                v-model="donorSearch"
                label="Search by organization, email, or Auth0 ID"
                prepend-inner-icon="fas fa-search"
                clearable
                hide-details
                class="mb-4"
                data-testid="donor-search"
              ></v-text-field>
              <v-data-table
                :headers="userHeaders"
                :items="users"
                :loading="loadingUsers"
                :search="donorSearch"
                item-key="auth0_id"
                :items-per-page="10"
                class="elevation-1"
                data-testid="donor-table"
                @click:row="selectDonor"
              ></v-data-table>
            </v-stepper-window-item>

            <!-- Step 2: Select SDK Key -->
            <v-stepper-window-item :value="2">
              <v-alert type="info" variant="tonal" class="mb-4">
                Donor: <strong>{{ donor?.email ?? '' }}</strong>
                ({{ donor?.organization ?? donor?.auth0_id ?? '' }})
                <v-btn variant="text" size="small" @click="resetToStep1" class="ml-2">Change</v-btn>
              </v-alert>

              <v-text-field
                v-model="keySearch"
                label="Search by name, bundle ID, or key value"
                prepend-inner-icon="fas fa-search"
                clearable
                hide-details
                class="mb-4"
                data-testid="key-search"
              ></v-text-field>

              <v-progress-circular v-if="loadingKeys" indeterminate color="primary" class="d-block mx-auto my-4"></v-progress-circular>

              <v-alert v-else-if="donorKeys.length === 0" type="warning" variant="tonal">
                This user has no SDK keys.
              </v-alert>

              <v-alert v-else-if="filteredKeys.length === 0" type="info" variant="tonal">
                No keys match your search.
              </v-alert>

              <v-list v-else>
                <v-list-item
                  v-for="key in filteredKeys"
                  :key="key.id"
                  :active="selectedKey?.id === key.id"
                  active-color="primary"
                  rounded="lg"
                  class="mb-1"
                  data-testid="sdk-key-item"
                  @click="selectKey(key)"
                >
                  <v-list-item-title>{{ key.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ key.bundle_id }}
                    <span class="ml-3 text-mono text-grey">{{ key.value.slice(0, 16) }}…</span>
                  </v-list-item-subtitle>
                  <template v-slot:append>
                    <v-icon v-if="selectedKey?.id === key.id" color="primary">fas fa-check-circle</v-icon>
                  </template>
                </v-list-item>
              </v-list>

              <div class="d-flex justify-end mt-4 ga-2">
                <v-btn variant="text" @click="resetToStep1">Back</v-btn>
                <v-btn
                  color="primary"
                  :disabled="!selectedKey"
                  data-testid="key-next-btn"
                  @click="currentStep = 3"
                >
                  Next
                </v-btn>
              </div>
            </v-stepper-window-item>

            <!-- Step 3: Select Recipient -->
            <v-stepper-window-item :value="3">
              <v-alert type="info" variant="tonal" class="mb-4">
                Transferring key <strong>{{ selectedKey?.name ?? '' }}</strong>
                from <strong>{{ donor?.email ?? '' }}</strong>
                <v-btn variant="text" size="small" @click="currentStep = 2" class="ml-2">Change Key</v-btn>
              </v-alert>

              <v-text-field
                v-model="recipientSearch"
                label="Search by organization, email, or Auth0 ID"
                prepend-inner-icon="fas fa-search"
                clearable
                hide-details
                class="mb-4"
                data-testid="recipient-search"
              ></v-text-field>
              <v-data-table
                :headers="userHeaders"
                :items="recipientUsers"
                :loading="loadingUsers"
                :search="recipientSearch"
                :row-props="recipientRowProps"
                item-key="auth0_id"
                :items-per-page="10"
                class="elevation-1"
                data-testid="recipient-table"
                @click:row="selectRecipientRow"
              ></v-data-table>

              <div class="d-flex justify-end mt-4 ga-2">
                <v-btn variant="text" @click="currentStep = 2">Back</v-btn>
                <v-btn
                  color="primary"
                  :disabled="!recipient"
                  data-testid="recipient-next-btn"
                  @click="currentStep = 4"
                >
                  Next
                </v-btn>
              </div>
            </v-stepper-window-item>

            <!-- Step 4: Confirm or Success -->
            <v-stepper-window-item :value="4">
              <template v-if="!transferComplete">
                <div class="text-subtitle-1 mb-4">Review Transfer</div>
                <v-table>
                  <tbody>
                    <tr>
                      <td class="text-medium-emphasis">Key Name</td>
                      <td><strong>{{ selectedKey?.name ?? '' }}</strong></td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">Bundle ID</td>
                      <td>{{ selectedKey?.bundle_id ?? '' }}</td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">From</td>
                      <td>{{ donor ? `${donor.email} (${donor.organization ?? donor.auth0_id})` : '' }}</td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">To</td>
                      <td>{{ recipient ? `${recipient.email} (${recipient.organization ?? recipient.auth0_id})` : '' }}</td>
                    </tr>
                  </tbody>
                </v-table>

                <div class="d-flex justify-end mt-6 ga-2">
                  <v-btn variant="text" @click="currentStep = 3">Back</v-btn>
                  <v-btn
                    color="primary"
                    :loading="transferring"
                    data-testid="confirm-transfer-btn"
                    @click="handleConfirm"
                  >
                    Transfer
                  </v-btn>
                </div>
              </template>

              <template v-else>
                <v-alert type="success" variant="tonal" class="text-center">
                  <div class="text-h6 mb-2">Transfer Complete</div>
                  <p>
                    Key <strong>{{ selectedKey?.name ?? '' }}</strong> has been transferred
                    from <strong>{{ donor?.email ?? '' }}</strong>
                    to <strong>{{ recipient?.email ?? '' }}</strong>.
                  </p>
                  <v-btn color="primary" class="mt-4" @click="resetAll">Transfer Another Key</v-btn>
                </v-alert>
              </template>
            </v-stepper-window-item>
          </v-stepper-window>
        </v-stepper>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="showSuccess" :timeout="3000" color="success">
      SDK key transferred successfully.
    </v-snackbar>
  </v-container>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-vue';
import { useStore } from 'vuex';

export default {
  name: 'TransferSdkKey',
  setup() {
    const store = useStore();
    const { getAccessTokenSilently } = useAuth0();

    const currentStep = ref(1);
    const users = ref([]);
    const loadingUsers = ref(true);
    const donorSearch = ref('');
    const donor = ref(null);
    const donorKeys = ref([]);
    const loadingKeys = ref(false);
    const keySearch = ref('');
    const recipientSearch = ref('');
    const selectedKey = ref(null);
    const recipient = ref(null);
    const showSuccess = ref(false);
    const transferComplete = ref(false);
    const transferring = ref(false);

    const userHeaders = [
      { title: 'Organization', key: 'organization' },
      { title: 'Email', key: 'email' },
      { title: 'Auth0 ID', key: 'auth0_id' },
    ];

    const recipientUsers = computed(() =>
      users.value.filter(u => !donor.value || u.auth0_id !== donor.value.auth0_id)
    );

    const filteredKeys = computed(() => {
      const query = keySearch.value.trim().toLowerCase();
      if (!query) return donorKeys.value;
      return donorKeys.value.filter(k =>
        k.name.toLowerCase().includes(query) ||
        k.bundle_id.toLowerCase().includes(query) ||
        k.value.toLowerCase().includes(query)
      );
    });

    const fetchUsers = async () => {
      loadingUsers.value = true;
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        users.value = response.data;
      } catch (e) {
        store.commit('SET_ERROR_MESSAGE', e.response?.data?.message || e.message || 'Failed to load users.');
        store.commit('SET_SHOW_ERROR_MODAL', true);
      } finally {
        loadingUsers.value = false;
      }
    };

    const fetchDonorKeys = async (auth0Id) => {
      loadingKeys.value = true;
      donorKeys.value = [];
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(
          `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/users/${auth0Id}/sdk-keys`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        donorKeys.value = response.data;
      } catch (e) {
        store.commit('SET_ERROR_MESSAGE', e.response?.data?.message || e.message || 'Failed to load SDK keys.');
        store.commit('SET_SHOW_ERROR_MODAL', true);
      } finally {
        loadingKeys.value = false;
      }
    };

    const selectDonor = (_, { item }) => {
      donor.value = item;
      selectedKey.value = null;
      recipient.value = null;
      keySearch.value = '';
      currentStep.value = 2;
      fetchDonorKeys(item.auth0_id);
    };

    const selectKey = (key) => {
      selectedKey.value = key;
      currentStep.value = 3;
    };

    const selectRecipient = (item) => {
      recipient.value = item;
    };

    const selectRecipientRow = (_, { item }) => {
      selectRecipient(item);
      currentStep.value = 4;
    };

    const recipientRowProps = ({ item }) => ({
      class: recipient.value && recipient.value.auth0_id === item.auth0_id ? 'bg-primary-lighten-4' : '',
    });

    const handleConfirm = async () => {
      transferring.value = true;
      try {
        const token = await getAccessTokenSilently();
        await axios.post(
          `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/sdk-keys/${selectedKey.value.id}/transfer`,
          { recipient_auth0_id: recipient.value.auth0_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        transferComplete.value = true;
        showSuccess.value = true;
      } catch (e) {
        store.commit('SET_ERROR_MESSAGE', e.response?.data?.message || e.message || 'Transfer failed.');
        store.commit('SET_SHOW_ERROR_MODAL', true);
      } finally {
        transferring.value = false;
      }
    };

    const resetToStep1 = () => {
      donor.value = null;
      selectedKey.value = null;
      recipient.value = null;
      donorKeys.value = [];
      donorSearch.value = '';
      keySearch.value = '';
      recipientSearch.value = '';
      currentStep.value = 1;
    };

    const resetAll = () => {
      transferComplete.value = false;
      resetToStep1();
    };

    onMounted(fetchUsers);

    return {
      currentStep,
      users,
      loadingUsers,
      userHeaders,
      donorSearch,
      donor,
      donorKeys,
      loadingKeys,
      keySearch,
      filteredKeys,
      selectedKey,
      recipient,
      recipientSearch,
      recipientUsers,
      showSuccess,
      transferComplete,
      transferring,
      selectDonor,
      selectKey,
      selectRecipient,
      selectRecipientRow,
      recipientRowProps,
      handleConfirm,
      resetToStep1,
      resetAll,
    };
  },
};
</script>
